/**
 * Integration role labels — a LAST-RESORT disambiguator for two rows that would
 * otherwise render the SAME name.
 *
 * ── WHAT PROBLEM THIS SOLVES (and why it now almost never fires) ─────────────
 * This mechanism exists for exactly one symptom, quoting its original header:
 * "rendering two bare 'Lark' cards is confusing. So we role-qualify them."
 * The root cause was never "a provider serves two purposes" — it was TWO ROWS
 * DISPLAYING THE SAME NAME. A user cannot act on two identical rows.
 *
 * That root cause is gone. The registry now names the two Lark providers
 * `lark` = "Lark Chat" and `lark_docs` = "Lark App" — distinct at a glance. So
 * a suffix on top of them is redundant noise: "Lark Chat · Docs" adds nothing a
 * reader needed, and it makes a plain, already-unambiguous card look like a
 * different product.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 * Qualify a row ONLY when it would COLLIDE with another row in the SAME
 * rendering — i.e. when two rows resolve to the same display name. Otherwise
 * render the plain registry name.
 *
 * The trigger is deliberately NOT "this provider appears in ≥2 role-bearing
 * groups" (what it used to be). That condition describes the DECLARATION; the
 * thing users actually suffer from is DISPLAY ambiguity. Keying on the real
 * symptom is what makes this mechanism SELF-RETIRING: it goes quiet the moment
 * the names disambiguate themselves, and it comes back automatically — with no
 * list of exceptions for anyone to maintain — if two names ever collide again.
 * `collidingProviders()` is that trigger, and it is the ONLY one; there is no
 * second decider to drift against it.
 *
 * As of today NO two registry display names collide, so in practice nothing is
 * qualified. That is the intended end state, not dead code: the tripwire
 * (below) asserts both that the real names are collision-free AND that the
 * qualifier still fires on a synthetic collision.
 *
 * IMPORTANT: this only changes the DISPLAY NAME. A role-qualified card keeps
 * the SAME provider `id` (e.g. the Lark card is id `lark` whichever roles it
 * serves), so it is backed by the ONE shared Lark connection — connecting/
 * selecting it drives the single underlying integration; only the label differs.
 * Nothing here touches deploy eligibility: the OR-groups, the gate and the
 * allowlist are entirely unaffected by how a row is labelled.
 *
 * ── TWO PLACES, ONE TRIPWIRE ────────────────────────────────────────────────
 * GROUP_ROLE_BY_MEMBERS below is a hand-maintained MIRROR of the backend's
 * OR-groups, and the self-host dashboard (selfhosted/ui/index.html, a build-less
 * vanilla-JS file that shares zero code with this app) keeps a SECOND mirror.
 * Nothing derives them, so `backend/__tests__/integration-role-map-drift.test.js`
 * asserts BOTH mirrors against the backend source of truth, in both directions:
 * every backend OR-group must be declared here (with a role, or an explicit
 * `null` meaning "deliberately roleless"), and every key here must still be a
 * live backend group. Adding/removing a group MEMBER changes the key and turns
 * that test red — which is exactly the drift that shipped dead labels when
 * `doc_source` gained `lark_docs` and neither mirror was updated.
 */

/** Canonical, order-independent key for an OR-group's member set. */
export function groupKey(members) {
  return [...new Set((members || []).filter((m) => typeof m === 'string'))]
    .sort()
    .join(',');
}

/**
 * OR-group PURPOSE → human role, keyed by the group's member set. The key
 * mirrors the backend meta-skills in skill-integrations.js (the member tuple is a
 * stable fingerprint of the meta-skill that emitted the group):
 *   doc_source      {google,notion,lark_docs}           → "Docs"  (a document source)
 *   chat_notify     {slack,lark}                        → "Chat"  (a chat/notify target)
 *   git-write / git {github,gitlab}                     → "Code"  (a code host)
 *
 * EXHAUSTIVE over the backend's OR-groups — a group with no useful role is
 * declared `null` rather than omitted, so "we decided this one needs no role"
 * and "somebody forgot" are distinguishable (and the drift tripwire can demand
 * a decision for every new group). Groups whose members are variants of the
 * SAME product are the roleless case: their two rows already carry different
 * registry names ("Lark Chat" / "Lark App", the two LinkedIn apps), so a role
 * adds nothing to disambiguate.
 */
const GROUP_ROLE_BY_MEMBERS = Object.freeze({
  'google,lark_docs,notion': 'Docs',
  'lark,slack': 'Chat',
  'github,gitlab': 'Code',
  // lark-attendance {lark_docs, lark} — the SAME product's two app credentials
  // (account-level app, chat app as the single-app fallback), already told apart
  // by their registry names. Roleless on purpose.
  'lark,lark_docs': null,
  // board-runner's tracker {jira, vikunja} — two different ISSUE TRACKERS,
  // one of which the board agent needs. Unlike the roleless pairs below they
  // are genuinely different products, so the row says which board it means.
  'jira,vikunja': 'Board',
  // linkedin {personal, business} — same product, two apps. Roleless on purpose.
  'linkedin_business,linkedin_personal': null,
});

/** The role for one OR-group (by its members), or null if it has none. */
export function roleForGroup(members) {
  return GROUP_ROLE_BY_MEMBERS[groupKey(members)] || null;
}

/**
 * The badge a row gets when it is one CANDIDATE for a REQUIRED one-of group —
 * `{ text, title }`, or null when the row is not one.
 *
 * WHAT IT FIXES (founder report, 2026-08-22, self-host first). board-runner
 * declares its tracker as `required: [{ any: ['jira','vikunja'] }]`. With BOTH
 * connected, the deploy modal drew two rows each badged "optional" — so the
 * screen said "two unrelated optional integrations" and the founder asked
 * "can I now pick 2 boards?". The answer is no: ONE board is correct (the
 * ledger, the tick budget and the lane routing are per-agent; two boards means
 * two agents).
 *
 * WHY IT SAID THAT, AND WHY THIS IS DISPLAY-ONLY. A member of a SATISFIED
 * OR-group is `optional` for GATE purposes — any one member is enough, so no
 * single one is individually required and the deploy must not demand the other.
 * That internal truth was printed verbatim. Marking these rows required again
 * would demand BOTH connections and break the one-of, so nothing here touches
 * `row.optional`; only the badge changes.
 *
 * The requirement's NAME is `roleForGroup` — the same map the role suffixes
 * use ("jira,vikunja" → "Board") — so there is no second label vocabulary and
 * the existing role-map drift tripwire keeps covering it. A roleless group
 * names no role rather than inventing one.
 *
 * @param {?{requiredGroup?:?string[]}} row  a MERGED row from buildIntegrationRows
 * @returns {?{text:string, title:string}}
 */
export function oneOfRequirement(row) {
  const group = row && row.requiredGroup;
  if (!Array.isArray(group) || group.length < 2) return null;
  const role = roleForGroup(group);
  return {
    text: role ? `${role} · one of these` : 'one of these',
    title: role
      ? `This agent needs one ${role}. Any one of these satisfies it — you do not need both.`
      : 'Any one of these satisfies this requirement — you do not need both.',
  };
}

/**
 * THE TRIGGER. Given the rows ACTUALLY BEING RENDERED TOGETHER and the resolver
 * that produces each row's display name, return the set of provider ids whose
 * name is shared with another row — the only rows a qualifier can help.
 *
 * Two properties this shape buys, both deliberate:
 *  • It measures the SYMPTOM (two rows, one name), not a proxy for it. The old
 *    trigger asked "does this provider appear in ≥2 role-bearing groups?", which
 *    is a fact about the declaration and stayed true long after the names had
 *    stopped colliding — that is how "Lark Chat · Docs" ended up decorating an
 *    already-unambiguous card.
 *  • It is computed from the SAME row list and the SAME name resolver the
 *    renderer uses, so what it measures and what the user sees cannot disagree.
 *    Pass the merged rows (one row per provider id) AFTER merging.
 *
 * @param {Array<{id:string}|string>} rows  the rows being rendered together
 * @param {(id:string)=>string} nameOf      the SAME resolver the renderer uses
 * @returns {Set<string>} provider ids whose display name collides with another row
 */
export function collidingProviders(rows, nameOf) {
  const idsByName = new Map(); // display name -> Set<provider id>
  for (const row of Array.isArray(rows) ? rows : []) {
    const id = typeof row === 'string' ? row : (row && row.id);
    if (typeof id !== 'string' || !id) continue;
    const name = String(nameOf ? nameOf(id) : id);
    if (!idsByName.has(name)) idsByName.set(name, new Set());
    idsByName.get(name).add(id);
  }
  const colliding = new Set();
  for (const ids of idsByName.values()) {
    if (ids.size > 1) for (const id of ids) colliding.add(id);
  }
  return colliding;
}

/**
 * The roles a base name does NOT already say, in order, deduped.
 *
 * The registry display name is itself often qualified — provider `lark` is named
 * "Lark Chat", `lark_docs` is "Lark App", `google` is "Google Docs" — so gluing
 * the role on unconditionally produces "Lark Chat Chat" / "Lark Chat Docs":
 * redundant at best, self-contradicting at worst. A role the name already
 * carries adds no information, so it is dropped. Matching is on whole words
 * (case-insensitive) so "Codecov" is never read as carrying the "Code" role.
 *
 * This is what keeps the label correct for FUTURE copy too: if `lark` is ever
 * renamed back to a bare "Lark", the dropped role comes back on its own.
 *
 * @param {string} baseName
 * @param {?string[]} roles
 * @returns {string[]}
 */
export function novelRoles(baseName, roles) {
  const said = new Set(
    String(baseName == null ? '' : baseName)
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean),
  );
  const out = [];
  for (const role of Array.isArray(roles) ? roles : []) {
    if (typeof role !== 'string' || !role) continue;
    if (said.has(role.toLowerCase())) continue;
    if (!out.includes(role)) out.push(role);
  }
  return out;
}

/**
 * The display label for one row: the provider's registry name, plus the roles
 * that name doesn't already express, as a SEPARATE clause after a middot —
 * "Lark Chat · Docs", "Lark Chat · Docs + Attendance". The middot matters: a
 * concatenated suffix reads as part of the brand name ("Lark Chat Docs" looks
 * like a third Lark product), a set-off clause reads as what the row is FOR.
 * No novel roles ⇒ the plain name, unchanged.
 *
 * @param {string} baseName  the provider's display name (from the registry)
 * @param {?string[]} roles  the roles this row serves
 * @returns {string}
 */
export function roleLabel(baseName, roles) {
  const novel = novelRoles(baseName, roles);
  return novel.length ? `${baseName} · ${novel.join(' + ')}` : baseName;
}

// NOTE: there is deliberately no second labeller here. An earlier
// `roleQualifiedName(baseName, id, members, …)` re-derived a role from ONE of a
// row's groups while `mergedRowLabel` used the row's ACCUMULATED roles — two
// deciders that could disagree, and did: a merged row whose `members` happened
// to point at a roleless group rendered plain even though it served a role.
// `roleLabel(baseName, row.roles)` is now the only way a label is formed.
